import type {
  PatientAccessMode,
  TenantRoleKey,
  TenantSessionBootstrapMode,
} from "@amplecare/contracts";
import {
  resolveTenantSessionBootstrapMode,
  resolveTenantSessionPatientAccessMode,
  resolveTenantSessionRoleKeys,
} from "@amplecare/contracts";

export type ClinicianWorkbenchTransportMode = "scaffold" | "http";

export interface MobileRuntimeConfig {
  clinicianWorkbenchTransportMode: ClinicianWorkbenchTransportMode;
  apiBaseUrl: string | null;
  sessionApiBaseUrl: string | null;
  requestTenantId: string;
  workforceSessionSubjectKey: string | null;
  requestSiteId: string | null;
  requestClinicianUserId: string;
  tenantSessionBootstrapMode: TenantSessionBootstrapMode;
  tenantSessionRoleKeys: TenantRoleKey[];
  tenantSessionPatientAccessMode: PatientAccessMode;
  tenantSessionSiteId: string | null;
  whiteLabelBrandKey: string | null;
}

interface MobileRuntimeConfigSource {
  clinicianWorkbenchTransportMode?: unknown;
  apiBaseUrl?: unknown;
  sessionApiBaseUrl?: unknown;
  requestTenantId?: unknown;
  workforceSessionSubjectKey?: unknown;
  requestSiteId?: unknown;
  requestClinicianUserId?: unknown;
  tenantSessionBootstrapMode?: unknown;
  tenantSessionRoleKeys?: unknown;
  tenantSessionPatientAccessMode?: unknown;
  tenantSessionSiteId?: unknown;
  whiteLabelBrandKey?: unknown;
  pathRuntimeConfigSources?: unknown;
}

function compactRuntimeConfigSource(
  source: MobileRuntimeConfigSource | null,
): MobileRuntimeConfigSource | null {
  if (!source) {
    return null;
  }

  const entries = Object.entries(source).filter(
    ([, value]) => value !== undefined,
  );

  return entries.length > 0
    ? (Object.fromEntries(entries) as MobileRuntimeConfigSource)
    : null;
}

function isRuntimeConfigRecord(
  value: unknown,
): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

export const mobileRuntimeConfigQueryParamNames = {
  clinicianWorkbenchTransportMode: "acTransport",
  apiBaseUrl: "acApiBaseUrl",
  sessionApiBaseUrl: "acSessionApiBaseUrl",
  requestTenantId: "acTenantId",
  workforceSessionSubjectKey: "acSessionSubjectKey",
  requestSiteId: "acSiteId",
  requestClinicianUserId: "acClinicianUserId",
  tenantSessionBootstrapMode: "acBootstrapMode",
  tenantSessionRoleKeys: "acRoleKeys",
  tenantSessionPatientAccessMode: "acPatientAccessMode",
  tenantSessionSiteId: "acSessionSiteId",
  whiteLabelBrandKey: "acBrand",
} as const;

const defaultRuntimeConfig: MobileRuntimeConfig = {
  clinicianWorkbenchTransportMode: "http",
  apiBaseUrl: null,
  sessionApiBaseUrl: null,
  requestTenantId: "tenant-unconfigured",
  workforceSessionSubjectKey: null,
  requestSiteId: "site-unconfigured",
  requestClinicianUserId: "clinician-unconfigured",
  tenantSessionBootstrapMode: "cookie-session",
  tenantSessionRoleKeys: ["clinician"],
  tenantSessionPatientAccessMode: "tenant-panel",
  tenantSessionSiteId: null,
  whiteLabelBrandKey: null,
};

const hostedWorkspacePathRuntimeConfigSources: Record<
  string,
  MobileRuntimeConfigSource
> = {
  "/": {
    requestClinicianUserId: "patient-workspace",
    requestSiteId: null,
    tenantSessionBootstrapMode: "cookie-session",
    tenantSessionPatientAccessMode: "none",
    tenantSessionRoleKeys: ["patient"],
    tenantSessionSiteId: null,
    whiteLabelBrandKey: null,
    workforceSessionSubjectKey: null,
  },
  "/admin": {
    requestClinicianUserId: "admin-unconfigured",
    requestSiteId: null,
    tenantSessionBootstrapMode: "bearer-token",
    tenantSessionPatientAccessMode: "none",
    tenantSessionRoleKeys: ["tenant-admin"],
    tenantSessionSiteId: null,
    whiteLabelBrandKey: null,
    workforceSessionSubjectKey: "admin-unconfigured-web",
  },
  "/clinician": {
    requestClinicianUserId: "clinician-unconfigured",
    requestSiteId: "site-unconfigured",
    tenantSessionBootstrapMode: "bearer-token",
    tenantSessionPatientAccessMode: "tenant-panel",
    tenantSessionRoleKeys: ["clinician"],
    tenantSessionSiteId: "site-unconfigured",
    workforceSessionSubjectKey: "clinician-unconfigured-web",
  },
  "/patient": {
    requestClinicianUserId: "patient-workspace",
    requestSiteId: null,
    tenantSessionBootstrapMode: "cookie-session",
    tenantSessionPatientAccessMode: "none",
    tenantSessionRoleKeys: ["patient"],
    tenantSessionSiteId: null,
    whiteLabelBrandKey: null,
    workforceSessionSubjectKey: null,
  },
};

const localPreviewWorkspacePathRuntimeConfigSources: Record<
  string,
  MobileRuntimeConfigSource
> = {
  "/": {
    clinicianWorkbenchTransportMode: "scaffold",
    requestClinicianUserId: "local-patient",
    requestSiteId: "site-local",
    tenantSessionBootstrapMode: "scaffold-header",
    tenantSessionPatientAccessMode: "none",
    tenantSessionRoleKeys: ["patient"],
    tenantSessionSiteId: "site-local",
    workforceSessionSubjectKey: "patient-local-web",
  },
  "/admin": {
    clinicianWorkbenchTransportMode: "scaffold",
    requestClinicianUserId: "local-admin",
    requestSiteId: null,
    tenantSessionBootstrapMode: "scaffold-header",
    tenantSessionPatientAccessMode: "none",
    tenantSessionRoleKeys: ["tenant-admin"],
    tenantSessionSiteId: null,
    whiteLabelBrandKey: null,
    workforceSessionSubjectKey: "admin-local-web",
  },
  "/clinician": {
    clinicianWorkbenchTransportMode: "scaffold",
    requestClinicianUserId: "local-clinician",
    requestSiteId: "site-local",
    tenantSessionBootstrapMode: "scaffold-header",
    tenantSessionPatientAccessMode: "tenant-panel",
    tenantSessionRoleKeys: ["clinician"],
    tenantSessionSiteId: "site-local",
    workforceSessionSubjectKey: "clinician-local-web",
  },
  "/patient": {
    clinicianWorkbenchTransportMode: "scaffold",
    requestClinicianUserId: "local-patient",
    requestSiteId: "site-local",
    tenantSessionBootstrapMode: "scaffold-header",
    tenantSessionPatientAccessMode: "none",
    tenantSessionRoleKeys: ["patient"],
    tenantSessionSiteId: "site-local",
    workforceSessionSubjectKey: "patient-local-web",
  },
};

const workspaceRuntimeConfigPathnames = new Set([
  "/",
  "/admin",
  "/clinician",
  "/patient",
]);

function getBrowserWindow(): Window | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window;
}

function getRuntimeConfigGlobalObject(): MobileRuntimeConfigSource | null {
  const runtimeConfig = (
    globalThis as typeof globalThis & {
      __AMPLECARE_RUNTIME_CONFIG__?: MobileRuntimeConfigSource;
    }
  ).__AMPLECARE_RUNTIME_CONFIG__;

  return isRuntimeConfigRecord(runtimeConfig)
    ? (runtimeConfig as MobileRuntimeConfigSource)
    : null;
}

function getRuntimeConfigGlobalSource(): MobileRuntimeConfigSource | null {
  const browserWindow = getBrowserWindow();

  if (
    browserWindow &&
    !isLocalPreviewHostname(browserWindow.location.hostname) &&
    !workspaceRuntimeConfigPathnames.has(
      resolveCanonicalWorkspacePathname(browserWindow.location.pathname),
    )
  ) {
    return null;
  }

  const runtimeConfig = getRuntimeConfigGlobalObject();

  if (!runtimeConfig) {
    return null;
  }

  const { pathRuntimeConfigSources: _ignoredPathSources, ...flatSource } =
    runtimeConfig;

  return compactRuntimeConfigSource(flatSource);
}

function readSearchParamValue(
  searchParams: URLSearchParams,
  key: string,
): string | undefined {
  const value = searchParams.get(key);

  return value != null && value.length > 0 ? value : undefined;
}

function getRuntimeConfigSearchSource(): MobileRuntimeConfigSource | null {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return null;
  }

  if (!isLocalPreviewHostname(browserWindow.location.hostname)) {
    return null;
  }

  const searchParams = new URLSearchParams(browserWindow.location.search);
  const searchSource: MobileRuntimeConfigSource = {
    clinicianWorkbenchTransportMode: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.clinicianWorkbenchTransportMode,
    ),
    whiteLabelBrandKey: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.whiteLabelBrandKey,
    ),
    apiBaseUrl: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.apiBaseUrl,
    ),
    sessionApiBaseUrl: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.sessionApiBaseUrl,
    ),
    requestTenantId: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.requestTenantId,
    ),
    workforceSessionSubjectKey: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.workforceSessionSubjectKey,
    ),
    requestSiteId: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.requestSiteId,
    ),
    requestClinicianUserId: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.requestClinicianUserId,
    ),
    tenantSessionBootstrapMode: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.tenantSessionBootstrapMode,
    ),
    tenantSessionRoleKeys: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.tenantSessionRoleKeys,
    ),
    tenantSessionPatientAccessMode: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.tenantSessionPatientAccessMode,
    ),
    tenantSessionSiteId: readSearchParamValue(
      searchParams,
      mobileRuntimeConfigQueryParamNames.tenantSessionSiteId,
    ),
  };

  return compactRuntimeConfigSource(searchSource);
}

function normalizeRuntimeConfigPathname(pathname: string): string {
  const normalizedPathname = pathname.replace(/\/+$/u, "").toLowerCase();

  return normalizedPathname.length > 0 ? normalizedPathname : "/";
}

export function resolveCanonicalWorkspacePathname(pathname: string): string {
  const normalizedPathname = normalizeRuntimeConfigPathname(pathname);

  if (workspaceRuntimeConfigPathnames.has(normalizedPathname)) {
    return normalizedPathname;
  }

  const firstPathSegment = normalizedPathname
    .split("/")
    .find((pathSegment) => pathSegment.length > 0);

  if (!firstPathSegment) {
    return "/";
  }

  const candidateWorkspacePathname = `/${firstPathSegment}`;

  return workspaceRuntimeConfigPathnames.has(candidateWorkspacePathname)
    ? candidateWorkspacePathname
    : normalizedPathname;
}

export function shouldCanonicalizeWorkspacePathname(pathname: string): boolean {
  const normalizedPathname = normalizeRuntimeConfigPathname(pathname);
  const canonicalPathname = resolveCanonicalWorkspacePathname(pathname);

  return (
    workspaceRuntimeConfigPathnames.has(canonicalPathname) &&
    canonicalPathname !== normalizedPathname
  );
}

function getRuntimeConfigGlobalPathSources(): Record<
  string,
  MobileRuntimeConfigSource
> | null {
  const runtimeConfig = getRuntimeConfigGlobalObject();
  const rawPathSources = runtimeConfig?.pathRuntimeConfigSources;

  if (!isRuntimeConfigRecord(rawPathSources)) {
    return null;
  }

  const normalizedEntries = Object.entries(rawPathSources)
    .map(([pathKey, source]) => {
      if (!isRuntimeConfigRecord(source)) {
        return null;
      }

      const normalizedSource = compactRuntimeConfigSource(
        source as MobileRuntimeConfigSource,
      );

      if (!normalizedSource) {
        return null;
      }

      return [
        normalizeRuntimeConfigPathname(pathKey),
        normalizedSource,
      ] as const;
    })
    .filter(
      (
        entry,
      ): entry is readonly [string, MobileRuntimeConfigSource] => entry != null,
    );

  return normalizedEntries.length > 0
    ? Object.fromEntries(normalizedEntries)
    : null;
}

function isLocalPreviewHostname(hostname: string): boolean {
  const normalizedHostname = hostname.trim().toLowerCase();

  return (
    normalizedHostname === "localhost" ||
    normalizedHostname === "127.0.0.1" ||
    normalizedHostname === "::1"
  );
}

function getRuntimeConfigPathSource(): MobileRuntimeConfigSource | null {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return null;
  }

  const builtInPathRuntimeConfigSources = isLocalPreviewHostname(
    browserWindow.location.hostname,
  )
    ? localPreviewWorkspacePathRuntimeConfigSources
    : hostedWorkspacePathRuntimeConfigSources;
  const globalPathRuntimeConfigSources = isLocalPreviewHostname(
    browserWindow.location.hostname,
  )
    ? null
    : getRuntimeConfigGlobalPathSources();
  const canonicalPathname = resolveCanonicalWorkspacePathname(
    browserWindow.location.pathname,
  );
  const pathSource = {
    ...(builtInPathRuntimeConfigSources[canonicalPathname] ?? {}),
    ...(globalPathRuntimeConfigSources?.[canonicalPathname] ?? {}),
  };

  return compactRuntimeConfigSource(pathSource);
}

function getRuntimeConfigSource(): MobileRuntimeConfigSource | null {
  const globalSource = getRuntimeConfigGlobalSource();
  const pathSource = getRuntimeConfigPathSource();
  const searchSource = getRuntimeConfigSearchSource();

  if (!globalSource && !pathSource && !searchSource) {
    return null;
  }

  return {
    ...(globalSource ?? {}),
    ...(pathSource ?? {}),
    ...(searchSource ?? {}),
  };
}

function resolveTransportMode(value: unknown): ClinicianWorkbenchTransportMode {
  return value === "scaffold" ? "scaffold" : "http";
}

function resolveApiBaseUrl(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function resolveSessionApiBaseUrl(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function resolveRequestTenantId(value: unknown): string {
  return typeof value === "string" && value.length > 0
    ? value
    : defaultRuntimeConfig.requestTenantId;
}

function resolveRequestSiteId(value: unknown): string | null {
  if (value === null) {
    return null;
  }

  return typeof value === "string" && value.length > 0
    ? value
    : defaultRuntimeConfig.requestSiteId;
}

function resolveWorkforceSessionSubjectKey(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function resolveRequestClinicianUserId(value: unknown): string {
  return typeof value === "string" && value.length > 0
    ? value
    : defaultRuntimeConfig.requestClinicianUserId;
}

function resolveRuntimeTenantSessionRoleKeys(
  value: unknown,
): TenantRoleKey[] {
  if (typeof value === "string") {
    return resolveTenantSessionRoleKeys(value);
  }

  if (Array.isArray(value)) {
    return resolveTenantSessionRoleKeys(value.join(","));
  }

  return defaultRuntimeConfig.tenantSessionRoleKeys;
}

function resolveTenantSessionSiteId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isHostedBrowserRuntime(): boolean {
  const browserWindow = getBrowserWindow();

  return browserWindow
    ? !isLocalPreviewHostname(browserWindow.location.hostname)
    : false;
}

function disableHostedScaffoldRuntime(
  config: MobileRuntimeConfig,
): MobileRuntimeConfig {
  if (!isHostedBrowserRuntime()) {
    return config;
  }

  return {
    ...config,
    clinicianWorkbenchTransportMode: "http",
    tenantSessionBootstrapMode:
      config.tenantSessionBootstrapMode === "scaffold-header"
        ? "cookie-session"
        : config.tenantSessionBootstrapMode,
  };
}

export function resolveMobileRuntimeConfig(): MobileRuntimeConfig {
  const source = getRuntimeConfigSource();

  if (!source) {
    return disableHostedScaffoldRuntime(defaultRuntimeConfig);
  }

  return disableHostedScaffoldRuntime({
    clinicianWorkbenchTransportMode: resolveTransportMode(
      source.clinicianWorkbenchTransportMode,
    ),
    apiBaseUrl: resolveApiBaseUrl(source.apiBaseUrl),
    sessionApiBaseUrl: resolveSessionApiBaseUrl(source.sessionApiBaseUrl),
    requestTenantId: resolveRequestTenantId(source.requestTenantId),
    workforceSessionSubjectKey: resolveWorkforceSessionSubjectKey(
      source.workforceSessionSubjectKey,
    ),
    requestSiteId: resolveRequestSiteId(source.requestSiteId),
    requestClinicianUserId: resolveRequestClinicianUserId(
      source.requestClinicianUserId,
    ),
    tenantSessionBootstrapMode: resolveTenantSessionBootstrapMode(
      typeof source.tenantSessionBootstrapMode === "string"
        ? source.tenantSessionBootstrapMode
        : null,
    ),
    tenantSessionRoleKeys: resolveRuntimeTenantSessionRoleKeys(
      source.tenantSessionRoleKeys,
    ),
    tenantSessionPatientAccessMode: resolveTenantSessionPatientAccessMode(
      typeof source.tenantSessionPatientAccessMode === "string"
        ? source.tenantSessionPatientAccessMode
        : null,
    ),
    tenantSessionSiteId: resolveTenantSessionSiteId(source.tenantSessionSiteId),
    whiteLabelBrandKey:
      typeof source.whiteLabelBrandKey === "string" &&
      source.whiteLabelBrandKey.trim().length > 0
        ? source.whiteLabelBrandKey.trim().toLowerCase()
        : null,
  });
}
